import React, { useState, useCallback, useEffect, useRef } from "react";

import TemplateTable from "../../components/TemplateTable";
import { ITableAppRender } from "../../components/TableApp";

import { format } from "date-fns";

import { Button, Label, Grid, Popup } from "semantic-ui-react";
import TrocaRepository, { ITrocaListModel } from "../../Repositories/ExchangeRepository";
import errors from "../../Utils/ExtractErrors";
import { toast } from "react-toastify";
import { ISearch } from "../../components/Search";
import { eStepTroca, ISearchResponse } from "../../interfaces";
import { IFields } from "../../components/Search/FilterBuilder";
import CadastrarTroca from "./CadastrarTroca";
import EditarTroca from "./EditarTroca";
import { GetUser } from "../../services/auth";
import StepTroca from "./StepsTroca";
import ModalYesNo from "../../components/ModalYesNo";

const Exchanges: React.FC = () => {

    const [list, setList] = useState<ITrocaListModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    const { role } = GetUser();

    const setStep = (step: eStepTroca) => {
        switch (step) {
            case eStepTroca.EnviadoParaEmpresa:
                return <Label ribbon icon="send" color="teal" content="ENV" />;
            case eStepTroca.NaoVeioNaPasta:
                return <Label ribbon icon="thumbs down" color="black" content="NVP" />;
            case eStepTroca.RecebidoPelaEmpresa:
                return <Label ribbon icon="industry" color="purple" content="EMP" />;
            case eStepTroca.EnviadoParaGerenteSetor:
                return <Label ribbon icon="truck" color="pink" content="GES" />;
            case eStepTroca.Devedor:
                return <Label ribbon icon="user times" color="red" content="DEV" />;
            case eStepTroca.Finalizado:
                return <Label ribbon icon="flag checkered" color="green" content="FIN" />;
            default:
                return <Label ribbon icon="dont" color="grey" content="???" />;
        }
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Situação",
            content: (x: ITrocaListModel) => setStep(x.step),
            textAlign: "left",
            width: 1
        },
        {
            header: "Data",
            content: (x: ITrocaListModel) => format(new Date(x.data), "dd/MM/yyyy"),
            textAlign: "center",
            width: 1
        },
        {
            header: "Representante",
            content: (x: ITrocaListModel) => x.representante,
            textAlign: "center",
            width: 4
        },
        {
            header: "Revendedor",
            content: (x: ITrocaListModel) => x.revendedor,
            width: 5
        },
        {
            header: "Produto",
            content: (x: ITrocaListModel) => x.produto,
            width: 3
        }
    ];

    const onDelete = async (id: string) => {
        try {

            await TrocaRepository.Delete(id);

            toast(`Troca apagada com sucesso.`, { position: "top-center", type: "success" });
            load();
        } catch (error) {
            return toast("❌ Erro ao apagar a troca.", { position: "top-center", type: "error" });
        }
    }
    const load = useCallback(async () => {
        setLoading(true);
        try {

            let response: ISearchResponse<ITrocaListModel>;

            if (refSearch.current.type === "simples") {
                response = await TrocaRepository.List({
                    search: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            } else {
                response = await TrocaRepository.List({
                    filter: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setList(response.list);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }
            const error = errors.getAllErrors(err.response.data);
            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }, []);

    const onChangeRecords = (value: number) => {
        refRecordsByPage.current = value;
        load();
    }

    const onPageChange = (pageNumber: string | number | undefined) => {
        refPageNumber.current = pageNumber;
        load();
    }

    const onSearch = ({ searchString, type }: ISearch) => {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;
        load();
    }

    const handleActions = (data: ITrocaListModel, index: number) => {
        let actions: JSX.Element[] = [<StepTroca trocaId={data.id} />];
        if (role === "Admin" || role === "Operador" || role === "GerenteS") {
            actions = [<EditarTroca trocaId={data.id} onSuccess={load} />,
            <ModalYesNo
                trigger={
                    role === "Admin" &&
                    <Button
                        icon="trash"
                        color="red"
                    />
                }
                header="Apagar Troca"
                content={`Deseja realmente apagar a troca do revendedor ${data.revendedor}`}
                onSuccess={() => onDelete(data.id || "")}
            />
                , ...actions];
        }
        return actions;
    }

    useEffect(() => { load() }, [load]);

    const fields: IFields[] = [
        { label: "Data", name: "Data", type: "date", roles: ["Admin", "Operador"] },
        { label: "Representante", name: "Representante.Nome", type: "string", roles: ["Admin", "Operador", "GerenteS", "Representante"] },
        { label: "Revendedor", name: "Revendedor.Nome", type: "string", roles: ["Admin", "Operador", "GerenteS", "Representante"] },
        { label: "Produto", name: "Produto.Nome", type: "string", roles: ["Admin", "Operador", "GerenteS", "Representante"] },
        {
            label: "Situação",
            name: "step",
            type: "enum",
            values: [
                { key: "ENV", text: "ENV", value: 0, label: { icon: "send", color: "teal", content: "ENV" } },
                { key: "EMP", text: "EMP", value: 1, label: { icon: "indrustry", color: "purple", content: "EMP" } },
                { key: "GES", text: "GES", value: 2, label: { icon: "truck", color: "pink", content: "GES" } },
                { key: "FIN", text: "FIN", value: 3, label: { icon: "flag checkered", color: "green", content: "FIN" } },
                { key: "NVP", text: "NVP", value: 4, label: { icon: "thumbs down", color: "black", content: "NVP" } },
                { key: "DEV", text: "DEV", value: 5, label: { icon: "user times", color: "red", content: "DEV" } }
            ],
            roles: ["Admin", "Operador", "GerenteS", "Representante"]
        }
    ];

    return (
        <TemplateTable
            title="Trocas"
            icon="sync alternate"
            insert={<>
                <Grid centered={false}>
                    <Grid.Column textAlign="right">
                        {
                            role === "Admin" || role === "Operador" ?
                                <>
                                    <Popup content='Cadastrado e Enviado' position="bottom center" trigger={
                                        <Button
                                            color="teal"
                                            icon="paper plane"
                                            onClick={() => onSearch({ searchString: "STEP == 0", type: "avancado" })}
                                        />
                                    } />
                                    <Popup content='Recebido na Empresa' position="bottom center" trigger={
                                        <Button
                                            color="purple"
                                            icon="industry"
                                            onClick={() => onSearch({ searchString: "STEP == 1", type: "avancado" })}
                                        />
                                    } />
                                    <Popup content='Trocado, Enviado para G.S' position="bottom center" trigger={
                                        <Button
                                            color="pink"
                                            icon="truck"
                                            onClick={() => onSearch({ searchString: "STEP == 2", type: "avancado" })}
                                        />
                                    } />
                                    <Popup content='Não veio na pasta' position="bottom center" trigger={
                                        <Button
                                            color="black"
                                            icon="thumbs down"
                                            onClick={() => onSearch({ searchString: "STEP == 4", type: "avancado" })}
                                        />
                                    } />
                                    <Popup content='Devedores' position="bottom center" trigger={
                                        <Button
                                            color="red"
                                            icon="user times"
                                            onClick={() => onSearch({ searchString: "STEP == 5", type: "avancado" })}
                                        />
                                    } />
                                    <Popup content='Finalizadas' position="bottom center" trigger={
                                        <Button
                                            color="green"
                                            icon="flag checkered"
                                            onClick={() => onSearch({ searchString: "STEP == 3", type: "avancado" })}
                                        />
                                    } />
                                    <Popup content='Limpar Filtro' position="bottom center" trigger={
                                        <Button
                                            color="grey"
                                            icon="erase"
                                            onClick={() => onSearch({ searchString: "", type: "simples" })}
                                        />
                                    } />
                                </>
                                :
                                role === "Representante" ? <CadastrarTroca onSuccess={load} /> : undefined
                        }
                    </Grid.Column>
                </Grid>
            </>
            }
            tableRender={tableRender}
            tableList={list}
            searchPlaceHolder="Representante/Revendedor/Produto"
            onSearch={onSearch}
            filterFields={fields}
            tableLoading={loading}
            tableActions={handleActions}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalRecords: refTotalRecords.current,
                totalPages: refTotalPages.current,
                onPageChange: onPageChange
            }}
            tableOnChangeRecords={onChangeRecords}
        />
    );
};

export default Exchanges;
